<div class="sg-layout">
  <!-- ─── Sticky in-page navigation ─────────────────────────────────────────── -->
  <nav class="sg-nav" aria-label="Style guide sections">
    @for (group of visibleNavGroups(); track group.label) {
      <div class="sg-nav__group">
        <p class="sg-nav__group-label">{{ group.label }}</p>
        <ul class="sg-nav__list">
          @for (link of group.links; track link.id) {
            <li>
              <a
                class="sg-nav__link"
                [href]="'#' + link.id"
                (click)="scrollToSection($event, link.id)"
                >{{ link.label }}</a
              >
            </li>
          }
        </ul>
      </div>
    }
  </nav>

  <div class="style-guide-page">
    <header class="sg-header">
      <h1 class="sg-header__title">UI Style Reference</h1>
      <p class="sg-header__sub">
        Components, patterns and design tokens for CapX Flow. Ordered by how often you'll reach for
        them — data-display patterns first, foundations last. Toggle dark mode via the header to
        preview both themes.
      </p>
      <div class="sg-persona">
        <span class="sg-persona__label" id="sg-persona-label">Viewing as</span>
        <mat-button-toggle-group
          [value]="persona()"
          (change)="setPersona($event.value)"
          aria-labelledby="sg-persona-label"
        >
          <mat-button-toggle value="all">Everyone</mat-button-toggle>
          @for (p of personas; track p.value) {
            <mat-button-toggle [value]="p.value">{{ p.label }}</mat-button-toggle>
          }
        </mat-button-toggle-group>
      </div>
    </header>

    <!-- ─── Page layout (per persona) ────────────────────────────────────────── -->
    <section id="page-layout" class="sg-section">
      <h2 class="sg-section__title">Page layout</h2>
      <p class="sg-section__desc">
        Every page body is one <code>.dash-shell</code> column: <code>.page-head</code> (breadcrumb,
        title, actions) first, then content. What surrounds the column depends on whose page it is.
        Never add a page-level <code>max-width</code> or <code>margin</code> of your own.
      </p>
      <h3 class="sg-section__subtitle">Page header on client and agent pages</h3>
      <p class="sg-section__desc">
        A title icon says which thing this page is about, and the subtitle says what the page is for
        — never just a count. Ops and Admin pages keep the plain header: people work through them
        all day, so they stay dense.
      </p>
      <header class="page-head" style="margin-bottom: var(--space-6)">
        <div class="page-head__bar">
          <div class="page-head__titles page-head__titles--icon">
            <span class="page-head__icon" aria-hidden="true"
              ><app-icon name="zap" [size]="24"
            /></span>
            <h1 class="page-head__title">Kickstart Cash</h1>
            <p class="page-head__subtitle">
              Your Kickstart Cash applications for this business, and where each one stands.
            </p>
          </div>
        </div>
      </header>

      <div class="sg-layouts">
        @if (persona() !== 'ops') {
          <figure class="sg-layout-demo">
            <div class="sg-layout-demo__frame" aria-hidden="true">
              <div class="sg-layout-demo__header"></div>
              <div class="sg-layout-demo__body sg-layout-demo__body--centred">
                <div class="sg-layout-demo__page">
                  <span class="sg-layout-demo__crumb"></span>
                  <span class="sg-layout-demo__title"></span>
                  <span class="sg-layout-demo__block"></span>
                  <span class="sg-layout-demo__block"></span>
                </div>
              </div>
            </div>
            <figcaption class="sg-layout-demo__caption">
              <strong>{{ persona() === 'all' ? 'Business, Agent, Admin' : personaLabel() }}</strong>
              — top nav only. <code>.dash-shell</code> centres a 1024px column. Admin's data-dense
              tools may opt into <code>.dash-shell--wide</code> (1200px).
            </figcaption>
          </figure>
        }
        @if (persona() === 'all' || persona() === 'ops') {
          <figure class="sg-layout-demo">
            <div class="sg-layout-demo__frame" aria-hidden="true">
              <div class="sg-layout-demo__header"></div>
              <div class="sg-layout-demo__body">
                <div class="sg-layout-demo__nav">
                  <span></span><span></span><span></span><span></span>
                </div>
                <div class="sg-layout-demo__page">
                  <span class="sg-layout-demo__crumb"></span>
                  <span class="sg-layout-demo__title"></span>
                  <span class="sg-layout-demo__block"></span>
                  <span class="sg-layout-demo__block"></span>
                </div>
              </div>
            </div>
            <figcaption class="sg-layout-demo__caption">
              <strong>Ops</strong> — the workspace sidebar (<code>.ops-shell</code>, 232px) is the
              page's left edge: the <code>.dash-shell</code> column starts beside it rather than
              centring in what's left, and runs to 1200px (Ops pages are data-dense).
              <em>What these terms mean</em> sits at every page's foot. Below 900px the sidebar
              becomes a scrolling strip above the page.
            </figcaption>
          </figure>
        }
      </div>
    </section>

    <!-- ════════════════════════════════════════════════════════════════════════
         OVERVIEW
         ════════════════════════════════════════════════════════════════════════ -->

    <!-- ─── Dashboard cards ──────────────────────────────────────────────────── -->
    <section id="dashboard-cards" class="sg-section" [hidden]="!shows('dashboard-cards')">
      <h2 class="sg-section__title">Dashboard cards</h2>
      <p class="sg-section__desc">
        KPI tiles for the dashboard surfaces (agent, ops). Each is a
        <code>.dash-kpi</code> tile — an icon badge, a tabular-figure value and an uppercase label.
        On the style guide they run <code>--compact</code> to trim vertical whitespace; dashboards
        use the default density.
      </p>

      <section class="dash-kpi-grid" aria-label="Portfolio at a glance">
        @for (card of dashboardCards; track card.label; let i = $index) {
          <div class="dash-kpi dash-kpi--compact dash-enter" [style.--dash-index]="i">
            <div class="dash-kpi__top">
              <span
                class="dash-kpi__icon"
                [class.dash-kpi__icon--success]="card.tone === 'success'"
                [class.dash-kpi__icon--warning]="card.tone === 'warning'"
              >
                <app-icon [name]="card.icon" [size]="18" />
              </span>
            </div>
            <div>
              <p class="dash-kpi__value">{{ card.value }}</p>
              <p class="dash-kpi__label">{{ card.label }}</p>
            </div>
          </div>
        }
      </section>
    </section>

    <!-- ─── Application entry (FLOW-394) ───────────────────────────────────── -->
    <section id="app-entry" class="sg-section" [hidden]="!shows('app-entry')">
      <h2 class="sg-section__title">Application entry</h2>
      <p class="sg-section__desc">
        A business — or its agent — reaches Kickstart Cash and Debtors Finance through the
        pre-application only. The dashboard carries one entry: <strong>Apply for funding</strong> on
        the first-run hero, <strong>New application</strong> once it has applied. Both start a new
        application at <code>/entity/:id/applications/new</code>; a draft is resumed from its
        <strong>Continue</strong> banner on the dashboard.
      </p>
      <div class="sg-callout sg-callout--warning">
        <app-icon name="alert-triangle" [size]="18" />
        <span>
          <strong>Direct applications are paused (2026-10-22, FLOW-394).</strong> Don't add an
          <em>Apply for Kickstart Cash</em> / <em>Apply for Debtors Finance</em> shortcut or a
          <em>New …</em> button anywhere; a bare <code>/entity/:id/kickstart/new</code> or
          <code>/entity/:id/df/new</code> is sent to the pre-application by
          <code>directApplicationGuard</code>, and the server refuses the matching POSTs. One flag
          per side, both <code>DIRECT_APPLICATIONS_PAUSED</code>. Resuming an existing draft
          (<code>?draft=</code>, <em>Continue your application</em>) is untouched.
        </span>
      </div>
      <p class="sg-section__desc">
        The submission page (<code>/entity/:id/applications/submission/:preAppId</code>) leads with
        what the client shared: <strong>Enquiry details</strong> first under the heading, then the
        outcome, <strong>What happens next</strong>, then the documents.
      </p>
    </section>

    <!-- ════════════════════════════════════════════════════════════════════════
         DATA DISPLAY
         ════════════════════════════════════════════════════════════════════════ -->

    <!-- ─── Tables ───────────────────────────────────────────────────────────── -->
    <section id="tables" class="sg-section" [hidden]="!shows('tables')">
      <h2 class="sg-section__title">Tables</h2>
      <p class="sg-section__desc">
        The core ops worklist surface. Numeric columns right-align with tabular figures; the status
        column uses <code>&lt;app-status-chip&gt;</code>; actions sit flush right.
      </p>

      <h3 class="sg-section__subtitle">Density</h3>
      <div class="sg-row">
        <button
          mat-stroked-button
          [class.sg-toggle--on]="tableDensity() === 'compact'"
          (click)="tableDensity.set('compact')"
        >
          Compact
        </button>
        <button
          mat-stroked-button
          [class.sg-toggle--on]="tableDensity() === 'default'"
          (click)="tableDensity.set('default')"
        >
          Default
        </button>
        <button
          mat-stroked-button
          [class.sg-toggle--on]="tableDensity() === 'comfortable'"
          (click)="tableDensity.set('comfortable')"
        >
          Comfortable
        </button>
      </div>

      <div class="sg-table-card">
        <table
          mat-table
          [dataSource]="demoRows"
          class="sg-table"
          [attr.data-density]="tableDensity()"
        >
          <ng-container matColumnDef="shortId">
            <th mat-header-cell *matHeaderCellDef>
              <span class="sg-th-sortable"
                >Reference <app-icon name="chevrons-up-down" [size]="14"
              /></span>
            </th>
            <td mat-cell *matCellDef="let row">
              <a class="sg-table__link" href="#tables" (click)="$event.preventDefault()">{{
                row.shortId
              }}</a>
            </td>
          </ng-container>

          <ng-container matColumnDef="applicant">
            <th mat-header-cell *matHeaderCellDef>Applicant</th>
            <td mat-cell *matCellDef="let row">{{ row.applicant }}</td>
          </ng-container>

          <ng-container matColumnDef="product">
            <th mat-header-cell *matHeaderCellDef>Product</th>
            <td mat-cell *matCellDef="let row">{{ row.product }}</td>
          </ng-container>

          <ng-container matColumnDef="amount">
            <th mat-header-cell *matHeaderCellDef class="sg-cell--num">
              <span class="sg-th-sortable"
                >Amount (ZAR) <app-icon name="chevron-down" [size]="14"
              /></span>
            </th>
            <td mat-cell *matCellDef="let row" class="sg-cell--num">{{ row.amount | number }}</td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let row"><app-status-chip [status]="row.status" /></td>
          </ng-container>

          <ng-container matColumnDef="submitted">
            <th mat-header-cell *matHeaderCellDef>Submitted</th>
            <td mat-cell *matCellDef="let row">{{ row.submitted }}</td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th
              mat-header-cell
              *matHeaderCellDef
              class="sg-cell--actions"
              aria-label="Actions"
            ></th>
            <td mat-cell *matCellDef="let row" class="sg-cell--actions">
              <button
                mat-icon-button
                class="sg-icon-btn"
                matTooltip="View"
                aria-label="View application"
              >
                <app-icon name="eye" [size]="18" />
              </button>
              <button
                mat-icon-button
                class="sg-icon-btn"
                [matMenuTriggerFor]="rowMenu"
                aria-label="More actions"
              >
                <app-icon name="more-vertical" [size]="18" />
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="tableColumns; sticky: true"></tr>
          <tr mat-row *matRowDef="let row; columns: tableColumns" class="sg-table__row"></tr>
        </table>
      </div>

      <mat-menu #rowMenu="matMenu">
        <button mat-menu-item><app-icon name="eye" [size]="16" /> View</button>
        <button mat-menu-item><app-icon name="pencil" [size]="16" /> Edit</button>
        <button mat-menu-item class="sg-menu-item--danger">
          <app-icon name="trash" [size]="16" /> Delete
        </button>
      </mat-menu>

      <h3 class="sg-section__subtitle">Loading — skeleton rows</h3>
      <p class="sg-section__desc">
        Skeleton rows preserve column widths so headers don't orphan. Never a centred spinner inside
        a table.
      </p>
      <div class="sg-table-card">
        <table class="sg-table sg-table--skeleton">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Applicant</th>
              <th>Product</th>
              <th class="sg-cell--num">Amount</th>
              <th>Status</th>
              <th>Submitted</th>
            </tr>
          </thead>
          <tbody>
            @for (r of skeletonRows; track r) {
              <tr>
                <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span></td>
                <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w80"></span></td>
                <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w40"></span></td>
                <td class="sg-cell--num">
                  <span
                    class="sg-skeleton sg-skeleton--text"
                    style="width: 50%; margin-left: auto"
                  ></span>
                </td>
                <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
                <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w70"></span></td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <h3 class="sg-section__subtitle">Empty — no data vs filtered</h3>
      <div class="sg-states-grid">
        <div class="sg-table-card sg-empty">
          <app-icon name="document" [size]="40" />
          <p class="sg-empty__title">No applications yet</p>
          <p class="sg-empty__desc">Applications submitted by this entity will appear here.</p>
          <button mat-flat-button><app-icon name="plus" [size]="18" /> Start an application</button>
        </div>
        <div class="sg-table-card sg-empty">
          <app-icon name="search" [size]="40" />
          <p class="sg-empty__title">No results</p>
          <p class="sg-empty__desc">No applications match your search and filters.</p>
          <button mat-stroked-button>Clear filters</button>
        </div>
      </div>

      <h3 class="sg-section__subtitle">Error — failed to load</h3>
      <div class="sg-table-card sg-empty sg-empty--error">
        <app-icon name="x-circle" [size]="40" />
        <p class="sg-empty__title">Couldn't load applications</p>
        <p class="sg-empty__desc">Something went wrong on our side.</p>
        <button mat-stroked-button><app-icon name="refresh" [size]="18" /> Try again</button>
      </div>
    </section>

    <!-- ─── Status chips ─────────────────────────────────────────────────────── -->
    <section id="status-chips" class="sg-section" [hidden]="!shows('status-chips')">
      <h2 class="sg-section__title">Status Chips</h2>
      <p class="sg-section__desc">
        Every platform status, rendered via <code>&lt;app-status-chip&gt;</code>. Labels and
        variants come from the <code>*_STATUS_META</code> map that owns each axis — never paraphrase
        a label. Colour is always paired with text (CapX accessibility rule).
      </p>
      <p class="sg-section__desc">
        These are grouped because the platform has no single status machine: a Kickstart facility,
        its application stage, and a Debtors Finance invoice are unrelated vocabularies that happen
        to share a chip. Pick from the group that matches the field you are rendering.
      </p>
      @for (group of statusGroups; track group.label) {
        <h3 class="sg-section__subtitle">{{ group.label }}</h3>
        <div class="sg-row sg-row--wrap">
          @for (status of group.statuses; track status) {
            <app-status-chip [status]="status" />
          }
        </div>
      }

      <h3 class="sg-section__subtitle">Variants</h3>
      <p class="sg-section__desc">
        Five badge variants back every status: <code>neutral</code>, <code>default</code>,
        <code>warning</code>, <code>success</code>, <code>destructive</code>.
      </p>
    </section>

    <!-- ─── Search & filters ─────────────────────────────────────────────────── -->
    <section id="search" class="sg-section" [hidden]="!shows('search')">
      <h2 class="sg-section__title">Search &amp; Filters</h2>

      <h3 class="sg-section__subtitle">Search field</h3>
      <div class="sg-search">
        <app-icon name="search" [size]="18" class="sg-search__icon" />
        <input
          class="sg-search__input"
          type="search"
          placeholder="Search applications…"
          [value]="searchTerm()"
          (input)="searchTerm.set($any($event.target).value)"
          aria-label="Search applications"
        />
        @if (searchTerm()) {
          <button class="sg-search__clear" (click)="clearSearch()" aria-label="Clear search">
            <app-icon name="x" [size]="16" />
          </button>
        }
      </div>

      <h3 class="sg-section__subtitle">Active filters</h3>
      @if (activeFilters().length) {
        <div class="sg-filter-chips">
          @for (filter of activeFilters(); track filter) {
            <span class="sg-filter-chip">
              {{ filter }}
              <button
                class="sg-filter-chip__remove"
                (click)="removeFilter(filter)"
                [attr.aria-label]="'Remove ' + filter"
              >
                <app-icon name="x" [size]="14" />
              </button>
            </span>
          }
          <button class="sg-link-btn" (click)="clearFilters()">Clear all</button>
        </div>
      } @else {
        <p class="sg-section__desc">No filters active. Removing the last chip hides this row.</p>
      }
    </section>

    <!-- ─── Pagination ───────────────────────────────────────────────────────── -->
    <section id="pagination" class="sg-section" [hidden]="!shows('pagination')">
      <h2 class="sg-section__title">Pagination</h2>
      <p class="sg-section__desc">
        Page sizes for ops worklists are <strong>25 / 50 / 100</strong> — not the Material default
        of 5/10/25. Hide the paginator entirely when results fit one page.
      </p>

      <div class="sg-table-card sg-paginator-wrap">
        <mat-paginator
          [length]="totalItems"
          [pageSize]="pageSize()"
          [pageSizeOptions]="[25, 50, 100]"
          (page)="pageSize.set($event.pageSize)"
          aria-label="Select page"
        />
      </div>

      <h3 class="sg-section__subtitle">Text-only summary</h3>
      <p class="sg-page-range">{{ pageRangeLabel() }}</p>
    </section>

    <!-- ─── Empty & loading states ───────────────────────────────────────────── -->
    <section id="states" class="sg-section" [hidden]="!shows('states')">
      <h2 class="sg-section__title">Empty &amp; Loading States</h2>

      <h3 class="sg-section__subtitle">Skeleton primitives</h3>
      <p class="sg-section__desc">
        Line widths are modifiers — <code>sg-skeleton--w40</code>, <code>w50</code>,
        <code>w60</code>, <code>w70</code>, <code>w80</code>, <code>w100</code> — never an inline
        <code>style="width: …"</code>.
      </p>
      <div class="sg-skeleton-demo">
        <span class="sg-skeleton sg-skeleton--heading"></span>
        <span class="sg-skeleton sg-skeleton--text sg-skeleton--w100"></span>
        <span class="sg-skeleton sg-skeleton--text sg-skeleton--w80"></span>
        <span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span>
        <div class="sg-row" style="margin-top: 8px">
          <span class="sg-skeleton sg-skeleton--avatar"></span>
          <span class="sg-skeleton sg-skeleton--avatar" style="width: 40px; height: 40px"></span>
        </div>
      </div>

      <h3 class="sg-section__subtitle">Card skeleton</h3>
      <div class="cards-row">
        <div class="sg-metric-card">
          <span class="sg-skeleton sg-skeleton--text sg-skeleton--w50"></span>
          <span class="sg-skeleton sg-skeleton--heading" style="width: 35%; height: 28px"></span>
          <span class="sg-skeleton sg-skeleton--text sg-skeleton--w40"></span>
        </div>
      </div>

      <h3 class="sg-section__subtitle">Empty-state variants</h3>
      <p class="sg-section__desc">
        A client's first empty state gets an illustration in place of the icon. Dense worklists keep
        the icon.
      </p>
      <div class="sg-states-grid">
        <div class="sg-empty sg-empty--bordered">
          <span
            class="sg-empty__art"
            style="--art-src: url(/assets/illustrations/applications.svg)"
            aria-hidden="true"
          ></span>
          <p class="sg-empty__title">No applications yet</p>
          <p class="sg-empty__desc">Applications for this business will appear here.</p>
        </div>
        <div class="sg-empty sg-empty--bordered">
          <app-icon name="building" [size]="40" />
          <p class="sg-empty__title">No businesses yet</p>
          <p class="sg-empty__desc">Register a business to get started.</p>
          <button mat-flat-button><app-icon name="plus" [size]="18" /> Add a business</button>
        </div>
        <div class="sg-empty sg-empty--bordered">
          <app-icon name="shield" [size]="40" />
          <p class="sg-empty__title">Nothing to show</p>
          <p class="sg-empty__desc">You don't have permission to create items here.</p>
        </div>
      </div>
    </section>

    <!-- ─── Iconography ──────────────────────────────────────────────────────── -->
    <section id="icons" class="sg-section" [hidden]="!shows('icons')">
      <h2 class="sg-section__title">Iconography</h2>
      <p class="sg-section__desc">
        Stroke icons from <code>&lt;app-icon&gt;</code> (Lucide, 24×24, <code>currentColor</code>).
        Icons inherit text colour — set colour on the host, not the icon. Naked icon-buttons need an
        <code>aria-label</code> and a tooltip.
      </p>

      <div class="sg-icon-grid">
        @for (icon of iconCatalogue; track icon.name) {
          <div class="sg-icon-cell">
            <app-icon [name]="icon.name" [size]="24" />
            <code class="sg-icon-cell__name">{{ icon.name }}</code>
            <span class="sg-icon-cell__purpose">{{ icon.purpose }}</span>
          </div>
        }
      </div>

      <h3 class="sg-section__subtitle">One concept, one icon</h3>
      <p class="sg-section__desc">
        Use the same icon every time a concept appears. A page's title icon comes from this list.
      </p>
      <div class="sg-icon-grid">
        @for (c of iconConcepts; track c.concept) {
          <div class="sg-icon-cell">
            <app-icon [name]="c.name" [size]="24" />
            <span class="sg-icon-cell__purpose">{{ c.concept }}</span>
          </div>
        }
      </div>

      <h3 class="sg-section__subtitle">Size scale</h3>
      <div class="sg-icon-sizes">
        @for (size of iconSizes; track size) {
          <div class="sg-icon-size">
            <app-icon name="document" [size]="size" />
            <span class="sg-icon-size__label">{{ size }}px</span>
          </div>
        }
      </div>

      <h3 class="sg-section__subtitle">Icon buttons</h3>
      <div class="sg-row">
        <button mat-icon-button class="sg-icon-btn" matTooltip="Edit" aria-label="Edit">
          <app-icon name="pencil" [size]="18" />
        </button>
        <button mat-icon-button class="sg-icon-btn" matTooltip="Download" aria-label="Download">
          <app-icon name="download" [size]="18" />
        </button>
        <button
          mat-icon-button
          class="sg-icon-btn sg-icon-btn--danger"
          matTooltip="Delete"
          aria-label="Delete"
        >
          <app-icon name="trash" [size]="18" />
        </button>
      </div>
    </section>

    <!-- ════════════════════════════════════════════════════════════════════════
         COMPONENTS
         ════════════════════════════════════════════════════════════════════════ -->

    <!-- ─── Buttons ──────────────────────────────────────────────────────────── -->
    <section id="buttons" class="sg-section" [hidden]="!shows('buttons')">
      <h2 class="sg-section__title">Buttons</h2>

      <h3 class="sg-section__subtitle">Variants</h3>
      <div class="sg-row">
        <button mat-flat-button>Primary action</button>
        <button mat-stroked-button>Secondary</button>
        <button mat-button>Ghost / text</button>
        <button mat-flat-button><app-icon name="plus" [size]="18" /> With icon</button>
      </div>

      <h3 class="sg-section__subtitle">Disabled state</h3>
      <div class="sg-row">
        <button mat-flat-button disabled>Primary disabled</button>
        <button mat-stroked-button disabled>Secondary disabled</button>
        <button mat-button disabled>Ghost disabled</button>
      </div>

      <h3 class="sg-section__subtitle">Loading state</h3>
      <div class="sg-row">
        <button mat-flat-button class="sg-btn-loading" disabled>
          <mat-spinner diameter="16" />
          Saving…
        </button>
      </div>
    </section>

    <!-- ─── Form Fields ──────────────────────────────────────────────────────── -->
    <section id="forms" class="sg-section" [hidden]="!shows('forms')">
      <h2 class="sg-section__title">Form Fields</h2>

      <h3 class="sg-section__subtitle">Default state</h3>
      <form [formGroup]="demoForm" class="demo-form">
        <mat-form-field appearance="outline">
          <mat-label>Text input</mat-label>
          <input matInput formControlName="text" placeholder="Placeholder text" />
          <mat-hint>Hint text providing extra context</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Select</mat-label>
          <mat-select formControlName="select">
            <mat-option value="a">Option A</mat-option>
            <mat-option value="b">Option B</mat-option>
            <mat-option value="c">Option C</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-checkbox formControlName="checkbox"
          >Checkbox label — 14px, matches form hint size</mat-checkbox
        >
      </form>

      <h3 class="sg-section__subtitle">Error state</h3>
      <form [formGroup]="errorForm" class="demo-form">
        <mat-form-field appearance="outline">
          <mat-label>Required field</mat-label>
          <input matInput formControlName="errorField" />
          @if (errorForm.controls.errorField.errors?.['required']) {
            <mat-error>This field is required</mat-error>
          }
        </mat-form-field>
      </form>
      <p class="sg-section__desc">
        A password input that carries our own show/hide toggle takes
        <code>class="has-password-toggle"</code> (global): it hides Edge's built-in reveal eye and
        clear ×, which otherwise sit beside ours. A bare password input keeps the browser's control.
      </p>
    </section>

    <!-- ─── Cards ────────────────────────────────────────────────────────────── -->
    <section id="cards" class="sg-section" [hidden]="!shows('cards')">
      <h2 class="sg-section__title">Cards</h2>

      <h3 class="sg-section__subtitle">Surface variants</h3>
      <div class="cards-row">
        <mat-card appearance="outlined">
          <mat-card-content class="demo-card-content">
            <span class="sg-card-type-tag">mat-card outlined</span>
            <h3 class="demo-card-content__heading">Card heading</h3>
            <p class="demo-card-content__body">
              Card body copy using default card surface and border tokens.
            </p>
            <button mat-flat-button>Primary action</button>
          </mat-card-content>
        </mat-card>

        <button type="button" class="sg-clickable-card">
          <span class="sg-card-type-tag">clickable card</span>
          <h3 class="demo-card-content__heading">Hover &amp; focus me</h3>
          <p class="demo-card-content__body">
            Lifts on hover, shows a focus ring on keyboard focus. Use for whole-row navigation.
          </p>
        </button>
      </div>

      <h3 class="sg-section__subtitle">Card with header &amp; footer</h3>
      <div class="cards-row">
        <div class="sg-panel">
          <div class="sg-panel__header">
            <div>
              <p class="sg-panel__title">Application APP-1042</p>
              <p class="sg-panel__subtitle">Thabo Trading CC</p>
            </div>
            <app-status-chip status="SUBMITTED" />
          </div>
          <div class="sg-panel__body">
            <p class="demo-card-content__body">
              Header carries title + status; footer carries actions.
            </p>
          </div>
          <div class="sg-panel__footer">
            <button mat-button>Cancel</button>
            <button mat-flat-button>Open</button>
          </div>
        </div>
      </div>

      <h3 class="sg-section__subtitle">Stat cards</h3>
      <div class="cards-row">
        <div class="sg-metric-card">
          <p class="sg-metric-card__label">Total Applications</p>
          <p class="sg-metric-card__value">42</p>
          <p class="sg-metric-card__sub">Last 30 days</p>
        </div>

        <div class="sg-metric-card">
          <p class="sg-metric-card__label">Approval Rate</p>
          <p class="sg-metric-card__value">68%</p>
          <p class="sg-metric-card__trend sg-metric-card__trend--up">
            <app-icon name="chevron-up" [size]="14" /> 4.2% vs last month
          </p>
        </div>

        <div class="sg-metric-card sg-metric-card--icon">
          <div class="sg-metric-card__icon"><app-icon name="document" [size]="20" /></div>
          <div>
            <p class="sg-metric-card__label">Pending review</p>
            <p class="sg-metric-card__value">7</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Badges & Role Pills ──────────────────────────────────────────────── -->
    <section id="badges" class="sg-section" [hidden]="!shows('badges')">
      <h2 class="sg-section__title">Badges &amp; Role Pills</h2>

      <h3 class="sg-section__subtitle">Status badge</h3>
      <div class="sg-row sg-row--wrap">
        <span class="sg-status-badge">Entity Admin</span>
        <span class="sg-status-badge">Entity Member</span>
      </div>

      <h3 class="sg-section__subtitle">Role pills (tokenised — dark-mode safe)</h3>
      <div class="sg-row sg-row--wrap">
        <span class="sg-role-pill sg-role-pill--admin">Admin</span>
        <span class="sg-role-pill sg-role-pill--member">Member</span>
        <span class="sg-role-pill sg-role-pill--agent">Agent</span>
      </div>

      <h3 class="sg-section__subtitle">AI mark</h3>
      <p class="sg-section__desc">
        <code>&lt;app-ai-mark&gt;</code> beside content the AI wrote or judged: once per block, on
        the column header in a table. Not on an engine figure, and not on a mention of AI.
      </p>
      <div class="sg-row sg-row--wrap">
        <app-ai-mark />
        <app-ai-mark [iconOnly]="true" />
      </div>

      <h3 class="sg-section__subtitle">What the AI read</h3>
      <p class="sg-section__desc">
        <code>.sg-ai-block</code> sets the model's reading apart from what the engine decided. The
        mark sits once in the block's header, so every line inside is the model's. Engine output
        stays on the plain surface beside it, unmarked.
      </p>
      <div class="sg-ai-block">
        <p><strong>The AI's view: Cannot say yet</strong> <app-ai-mark /></p>
        <p>The revenue in the statements is steady, but trading history is not confirmed.</p>
      </div>
    </section>

    <!-- ════════════════════════════════════════════════════════════════════════
         FEEDBACK
         ════════════════════════════════════════════════════════════════════════ -->

    <!-- ─── Collapsible panel ────────────────────────────────────────────────── -->
    <section id="collapsible" class="sg-section" [hidden]="!shows('collapsible')">
      <h2 class="sg-section__title">Collapsible panel</h2>
      <p class="sg-section__desc">
        <code>&lt;app-collapsible-panel&gt;</code> — an <code>.sg-panel</code> whose whole head is
        the toggle. For reference material that supports a decision rather than drives it (the
        folded reference on the Ops pre-application page). Starts closed unless
        <code>startOpen</code>.
      </p>
      <app-collapsible-panel title="Reference" summary="3 items" testId="sg-demo">
        <p class="sg-type-body-sm">
          Content shows when the head is pressed. The head is a real button: keyboard, focus ring
          and <code>aria-expanded</code> come for free.
        </p>
      </app-collapsible-panel>
    </section>

    <!-- ─── Glossary terms (DOC-80) ──────────────────────────────────────────── -->
    <section id="terms" class="sg-section" [hidden]="!shows('terms')">
      <h2 class="sg-section__title">Glossary terms</h2>
      <p class="sg-section__desc">
        <code>&lt;app-term key="…"&gt;</code> explains a rulebook term where it is used on an Ops
        page. The words stay; the first use on a page carries a <code>?</code> mark, later uses a
        dotted underline. Click, hover or focus opens the pop-up; the text comes from the glossary
        (DOC-80), never from the component.
      </p>
      <p class="sg-type-body">
        The <app-term key="confidence-band">confidence band</app-term> sits beside the
        <app-term key="recommendation">recommendation</app-term>, and a second mention of the
        <app-term key="confidence-band">confidence band</app-term> is underlined only.
      </p>
    </section>

    <!-- ─── Toasts ───────────────────────────────────────────────────────────── -->
    <section id="toasts" class="sg-section" [hidden]="!shows('toasts')">
      <h2 class="sg-section__title">Toasts / Snackbars</h2>
      <p class="sg-section__desc">
        Non-blocking confirmation, bottom-right. 4s success/info, 6s error. Never use a toast for
        destructive confirmation (use a dialog) or validation errors (use inline).
      </p>
      <div class="sg-row">
        <button mat-flat-button (click)="showToast('success')">Show success</button>
        <button mat-stroked-button (click)="showToast('error')">Show error</button>
        <button mat-stroked-button (click)="showToast('info')">Show info</button>
      </div>
    </section>

    <!-- ─── Confirm dialogs ──────────────────────────────────────────────────── -->
    <section id="dialogs" class="sg-section" [hidden]="!shows('dialogs')">
      <h2 class="sg-section__title">Confirmation Dialogs</h2>
      <p class="sg-section__desc">
        Backed by the shared <code>ConfirmDialogComponent</code>. Title is a question (<em
          >"Delete business?"</em
        >) — never "Are you sure?". Confirm label matches the verb; cancel is always "Cancel".
        Authority-gated actions name the tier. Ops action dialogs stay open while the write is in
        flight and close only once it succeeds — a failed write keeps the dialog and whatever was
        typed into it. Dismissable throughout via Cancel, the X, Escape or the overlay.
      </p>
      <div class="sg-dialog-demo">
        <p class="sg-dialog-demo__title">Delete business?</p>
        <p class="sg-dialog-demo__body">
          This cannot be undone. The business record and all its applications will be archived.
        </p>
        <div class="sg-dialog-demo__actions">
          <button mat-button>Cancel</button>
          <button mat-flat-button class="sg-btn--danger">Delete</button>
        </div>
      </div>
    </section>

    <!-- ─── Form modals ──────────────────────────────────────────────────────── -->
    <section id="modals" class="sg-section" [hidden]="!shows('modals')">
      <h2 class="sg-section__title">Form Modals</h2>
      <p class="sg-section__desc">
        Every modal with a form is built on the shared
        <code>&lt;app-modal&gt;</code> (<code>shared/modal/modal.ts</code>), opened with
        <code>MatDialog</code>. Your dialog component owns the fields and the write; the wrapper
        owns everything else.
      </p>
      <div class="sg-modal-demo">
        <button mat-flat-button type="button" (click)="openModalDemo()" data-testid="sg-modal-open">
          Open form modal
        </button>
        <button
          mat-stroked-button
          type="button"
          (click)="openModalDemo(modalFieldRefusal)"
          data-testid="sg-modal-open-refused-field"
        >
          Save refused: one field
        </button>
        <button
          mat-stroked-button
          type="button"
          (click)="openModalDemo(modalActRefusal)"
          data-testid="sg-modal-open-refused-act"
        >
          Save refused: whole act
        </button>
      </div>

      <h3 class="sg-section__subtitle">Usage</h3>
      <pre class="sg-modal-usage"><code>{{ modalUsage }}</code></pre>

      <h3 class="sg-section__subtitle">Rules</h3>
      <ul class="sg-list">
        <li>
          <strong>Close only on success.</strong> The modal closes once the write succeeds. A
          refused save keeps the modal open with the input kept.
        </li>
        <li>
          <strong>A refusal shows next to what it is about.</strong> If the server names a field,
          the message goes on that field. If it refuses the whole act, it goes beside the button.
          <code>placeRefusal</code> sorts them.
        </li>
        <li>
          <strong>Always dismissable.</strong> Cancel, the X, Escape and the overlay all work, even
          during a write. Never set <code>disableClose</code>. A form holding typed work may ask
          "Discard what you typed?" first, through <code>guardTypedInput</code> and
          <code>[dismissWith]</code>. The report form keeps its own rule: it closes only on Cancel
          or the X, because it hides during a screenshot.
        </li>
        <li>
          <strong>Validate on submit.</strong> Leave Save enabled and show errors under the fields
          when it is pressed.
        </li>
        <li>
          <strong>Name the act.</strong> The submit label matches the verb in the heading. Cancel is
          always "Cancel". A success toast follows when the modal closes.
        </li>
        <li>
          <strong>Stated facts aren't overwritten.</strong> Saving over a record suits settings and
          contact details. A declared fact gets a new row that names the one it corrects, so word
          the modal "Correct this entry", not "Update" (N8).
        </li>
        <li>
          <strong>A modal that only reads</strong> leaves out <code>submitLabel</code>: same frame,
          no form, one Close button. Its own acts go in a <code>modalFooterStart</code> element.
        </li>
        <li><strong>Yes/no questions</strong> use <code>ConfirmDialogComponent</code> instead.</li>
      </ul>
    </section>

    <!-- ─── Error banners ────────────────────────────────────────────────────── -->
    <section id="errors" class="sg-section" [hidden]="!shows('errors')">
      <h2 class="sg-section__title">Error Banners</h2>

      <h3 class="sg-section__subtitle">Status banners</h3>
      <div class="status-banners">
        <div class="sg-banner sg-banner--error">
          <app-icon name="x-circle" [size]="18" />
          <span><strong>Error</strong> — Something went wrong. Please try again.</span>
        </div>
        <div class="sg-banner sg-banner--warning">
          <app-icon name="alert-triangle" [size]="18" />
          <span
            ><strong>Warning</strong> — This application can't move to that status right now.</span
          >
        </div>
        <div class="sg-banner sg-banner--success">
          <app-icon name="check" [size]="18" />
          <span><strong>Success</strong> — Your changes have been saved.</span>
        </div>
        <div class="sg-banner sg-banner--info">
          <app-icon name="info" [size]="18" />
          <span><strong>Info</strong> — Take note of this message.</span>
        </div>
      </div>
    </section>

    <!-- ════════════════════════════════════════════════════════════════════════
         NAVIGATION
         ════════════════════════════════════════════════════════════════════════ -->

    <!-- ─── In-form refusal (FLOW-378) ───────────────────────────────────────── -->
    <section id="in-form-refusal" class="sg-section" [hidden]="!shows('in-form-refusal')">
      <h2 class="sg-section__title">In-form refusal</h2>
      <p class="sg-section__desc">
        A server refusal of an act keeps the form open with its input, and shows the server's own
        words <em>inside the form</em> — not in the banner at the top of the page, where the user
        who pressed Save never sees it. A refusal about one field goes on that field; a refusal of
        the whole act, like this one, goes beside the button. The page-level banner stays for load
        errors.
      </p>
      <div class="demo-form demo-refusal-form">
        <mat-form-field appearance="outline">
          <mat-label>Approved amount (ZAR)</mat-label>
          <input matInput value="350000" readonly />
        </mat-form-field>
        <div class="sg-row">
          <button mat-flat-button color="primary" type="button">Approve and issue offer</button>
          <button mat-button type="button">Cancel</button>
        </div>
        <div class="demo-refusal" role="alert">
          The assessment is out of date — run it before issuing an offer.
        </div>
      </div>
    </section>

    <!-- ─── Report widget ────────────────────────────────────────────────────── -->
    <section id="report" class="sg-section" [hidden]="!shows('report')">
      <h2 class="sg-section__title">Report widget</h2>
      <p class="sg-section__desc">
        <code>&lt;app-report-widget&gt;</code> sits once in <code>app.html</code>: the nine-dot
        launcher bottom-right on every signed-in page, opening <em>Report a bug</em> /
        <em>Suggest an improvement</em>. The dialog hides itself
        (<code>.report-dialog--hidden</code>, global) while the browser's screen-capture picker is
        up, so the screenshot shows the page and not the dialog. Don't add a second feedback entry
        point to a page.
      </p>
    </section>

    <!-- ─── Breadcrumbs ──────────────────────────────────────────────────────── -->
    <section id="breadcrumbs" class="sg-section" [hidden]="!shows('breadcrumbs')">
      <h2 class="sg-section__title">Breadcrumbs</h2>
      <nav class="sg-breadcrumbs" aria-label="Breadcrumb">
        <a href="#breadcrumbs" (click)="$event.preventDefault()">Home</a>
        <app-icon name="chevron-right" [size]="14" />
        <a href="#breadcrumbs" (click)="$event.preventDefault()">Thabo Trading CC</a>
        <app-icon name="chevron-right" [size]="14" />
        <span aria-current="page">Applications</span>
      </nav>
    </section>

    <!-- ─── Tabs ─────────────────────────────────────────────────────────────── -->
    <section id="tabs" class="sg-section" [hidden]="!shows('tabs')">
      <h2 class="sg-section__title">Tabs</h2>
      <mat-tab-group class="sg-tabs">
        <mat-tab label="Details"><p class="sg-tab-body">Application details panel.</p></mat-tab>
        <mat-tab label="Documents"><p class="sg-tab-body">Uploaded documents list.</p></mat-tab>
        <mat-tab label="History"><p class="sg-tab-body">Status timeline.</p></mat-tab>
        <mat-tab label="Assessment"><p class="sg-tab-body">BRE assessment output.</p></mat-tab>
      </mat-tab-group>
    </section>

    <!-- ─── Stepper ──────────────────────────────────────────────────────────── -->
    <section id="stepper" class="sg-section" [hidden]="!shows('stepper')">
      <h2 class="sg-section__title">Stepper / Wizard</h2>
      <p class="sg-section__desc">
        The application flow. Step labels are short nouns. POPIA consent is always its own discrete
        step.
      </p>
      <ol class="sg-steps">
        @for (step of steps; track step.label; let i = $index) {
          <li class="sg-step" [attr.data-state]="step.state">
            <span class="sg-step__marker">
              @if (step.state === 'complete') {
                <app-icon name="check" [size]="14" />
              } @else {
                {{ i + 1 }}
              }
            </span>
            <span class="sg-step__label">{{ step.label }}</span>
          </li>
        }
      </ol>
    </section>

    <!-- ─── Stage row ────────────────────────────────────────────────────────── -->
    <section id="stage-row" class="sg-section" [hidden]="!shows('stage-row')">
      <h2 class="sg-section__title">Stage row</h2>
      <p class="sg-section__desc">
        An application's progress by stage (DOC-89-A §5):
        <em>Submitted → Being assessed → Offer → Signed → Paid out</em>, the same row for the
        client, the agent and Ops. The row is the back end's <code>stage_row</code>; the screen
        draws it and works nothing out. The reached step is filled and the ones before it ticked —
        the marking is the shape, never a colour. No percentage, no ending: a declined, lapsed or
        withdrawn application shows the stage it reached, and the page's own status line says what
        happened.
      </p>
      <app-stage-row [row]="stageRow" />
    </section>

    <!-- ─── Menus ────────────────────────────────────────────────────────────── -->
    <section id="menus" class="sg-section" [hidden]="!shows('menus')">
      <h2 class="sg-section__title">Menus</h2>
      <div class="sg-row">
        <button mat-stroked-button [matMenuTriggerFor]="demoMenu">
          Actions <app-icon name="chevron-down" [size]="16" />
        </button>
      </div>
      <mat-menu #demoMenu="matMenu">
        <button mat-menu-item><app-icon name="eye" [size]="16" /> View</button>
        <button mat-menu-item><app-icon name="pencil" [size]="16" /> Edit</button>
        <button mat-menu-item><app-icon name="download" [size]="16" /> Export</button>
        <button mat-menu-item class="sg-menu-item--danger">
          <app-icon name="trash" [size]="16" /> Delete
        </button>
      </mat-menu>
    </section>

    <!-- ════════════════════════════════════════════════════════════════════════
         DETAIL PATTERNS
         ════════════════════════════════════════════════════════════════════════ -->

    <!-- ─── Fact lists ───────────────────────────────────────────────────────── -->
    <section id="facts" class="sg-section" [hidden]="!shows('facts')">
      <h2 class="sg-section__title">Fact Lists</h2>
      <p class="sg-section__desc">
        Structured data via <code>&lt;dl&gt;</code>. Currency as <code>ZAR</code> with separators;
        missing values as <code>—</code> (never "N/A" or blank).
      </p>
      <dl class="sg-facts">
        <div class="sg-facts__row">
          <dt>Product</dt>
          <dd>Kickstart Cash (KS)</dd>
        </div>
        <div class="sg-facts__row">
          <dt>Amount requested</dt>
          <dd>ZAR 250,000</dd>
        </div>
        <div class="sg-facts__row">
          <dt>Term</dt>
          <dd>12 months</dd>
        </div>
        <div class="sg-facts__row">
          <dt>Facility fee</dt>
          <dd>28% (Standard)</dd>
        </div>
        <div class="sg-facts__row">
          <dt>Submitted</dt>
          <dd>12 Jun 2026</dd>
        </div>
        <div class="sg-facts__row">
          <dt>Decision date</dt>
          <dd>—</dd>
        </div>
      </dl>
    </section>

    <!-- ─── Status timeline ──────────────────────────────────────────────────── -->
    <section id="timeline" class="sg-section" [hidden]="!shows('timeline')">
      <h2 class="sg-section__title">Status Timeline</h2>
      <p class="sg-section__desc">
        Append-only audit trail. Actor uses authority-tier labels ("Analyst", "Credit Committee") —
        never "Admin" or "Staff". System events read "CapX system".
      </p>
      <ol class="sg-timeline">
        @for (event of timeline; track event.date) {
          <li class="sg-timeline__item">
            <span class="sg-timeline__dot"></span>
            <div class="sg-timeline__content">
              <div class="sg-timeline__head">
                <app-status-chip [status]="event.status" />
                <span class="sg-timeline__date">{{ event.date }}</span>
              </div>
              <p class="sg-timeline__actor">{{ event.actor }}</p>
              @if (event.note) {
                <p class="sg-timeline__note">{{ event.note }}</p>
              }
            </div>
          </li>
        }
      </ol>
    </section>

    <!-- ─── Offer panel ──────────────────────────────────────────────────────── -->
    <section id="offer" class="sg-section" [hidden]="!shows('offer')">
      <h2 class="sg-section__title">Offer Panel</h2>
      <div class="sg-callout sg-callout--warning">
        <app-icon name="alert-triangle" [size]="18" />
        <span>
          <strong>Domain note:</strong> KS offers surface a <strong>Facility fee</strong> (28%
          Standard / 24% Preferred), DF offers a <strong>Discount rate</strong> — not an interest
          rate. The current <code>Offer.interest_rate_pa</code> field is a carry-over from the old
          generic model and should be corrected in a follow-up; this panel documents the intended
          labels.
        </span>
      </div>
      <div class="sg-panel sg-offer">
        <div class="sg-panel__header">
          <p class="sg-panel__title">Offer issued</p>
          <app-status-chip status="PENDING" />
        </div>
        <div class="sg-panel__body">
          <dl class="sg-facts">
            <div class="sg-facts__row">
              <dt>Amount</dt>
              <dd>ZAR 250,000</dd>
            </div>
            <div class="sg-facts__row">
              <dt>Term</dt>
              <dd>12 months</dd>
            </div>
            <div class="sg-facts__row">
              <dt>Facility fee</dt>
              <dd>28% (Standard)</dd>
            </div>
            <div class="sg-facts__row">
              <dt>Expires</dt>
              <dd>30 Jun 2026</dd>
            </div>
          </dl>
        </div>
        <div class="sg-panel__footer">
          <button mat-stroked-button class="sg-btn--danger">Decline</button>
          <button mat-flat-button><app-icon name="check" [size]="18" /> Accept offer</button>
        </div>
      </div>
    </section>

    <!-- ─── Document upload ──────────────────────────────────────────────────── -->
    <section id="upload" class="sg-section" [hidden]="!shows('upload')">
      <h2 class="sg-section__title">Document Upload</h2>
      <div class="sg-upload">
        <app-icon name="upload" [size]="32" />
        <p class="sg-upload__title">Attach a document</p>
        <p class="sg-upload__hint">PDF, PNG or JPG · max 100 MB</p>
        <button mat-stroked-button><app-icon name="upload" [size]="18" /> Choose file</button>
      </div>

      <h3 class="sg-section__subtitle">Uploaded files</h3>
      <ul class="sg-doc-list">
        <li class="sg-doc-item">
          <app-icon name="document" [size]="18" />
          <span class="sg-doc-item__name">bank-statement-may.pdf</span>
          <span class="sg-doc-item__meta">Bank statement · 2.4 MB</span>
          <app-icon name="check" [size]="18" class="sg-doc-item__ok" />
        </li>
        <li class="sg-doc-item sg-doc-item--error">
          <app-icon name="document" [size]="18" />
          <span class="sg-doc-item__name">financials.docx</span>
          <span class="sg-doc-item__meta sg-doc-item__meta--error"
            >File type not supported — upload PDF, PNG or JPG</span
          >
        </li>
      </ul>
    </section>

    <!-- ─── Role-gated UI ────────────────────────────────────────────────────── -->
    <section id="role-gating" class="sg-section" [hidden]="!shows('role-gating')">
      <h2 class="sg-section__title">Role-Gated UI</h2>
      <p class="sg-section__desc">
        Default: hide actions the user can't perform. Exception: authority-gated actions stay
        <em>visible but disabled</em> with a tooltip naming the required tier — so an analyst knows
        to escalate.
      </p>
      <div class="sg-row">
        <button mat-flat-button matTooltip="Requires Credit Committee approval" disabled>
          Approve above R1m
        </button>
        <button mat-stroked-button matTooltip="Attach documents before submitting" disabled>
          Submit
        </button>
      </div>
    </section>

    <!-- ════════════════════════════════════════════════════════════════════════
         UTILITIES
         ════════════════════════════════════════════════════════════════════════ -->

    <!-- ─── Outstanding items by stage (FLOW-370) ────────────────────────────── -->
    <section id="outstanding" class="sg-section" [hidden]="!shows('outstanding')">
      <h2 class="sg-section__title">Outstanding items by stage</h2>
      <p class="sg-section__desc">
        What still stands between a file and its next act, grouped by the stage the back end stamps
        on each item (<code>BEFORE_DECISION</code> / <code>BEFORE_PAYOUT</code>) — the screen never
        decides the stage. On the Ops page the groups read <em>Before a decision</em> and
        <em>Before payout</em>; the business's card asks only for rule acts, under one heading.
      </p>
      <div class="demo-outstanding">
        <div class="demo-outstanding__group">
          <h4 class="demo-outstanding__stage">Before a decision</h4>
          <ul class="sg-list">
            <li>The business confirms every bank account is uploaded</li>
            <li>Declared funder: security question unanswered</li>
          </ul>
        </div>
        <div class="demo-outstanding__group">
          <h4 class="demo-outstanding__stage">Before payout</h4>
          <ul class="sg-list">
            <li>SARS tax compliance PIN</li>
            <li>Debit order mandate confirmed</li>
          </ul>
        </div>
      </div>
      <mat-card appearance="outlined" class="demo-outstanding__client">
        <mat-card-content>
          <h3 class="demo-card-content__heading">To keep your application moving, we need:</h3>
          <ul class="sg-list">
            <li>
              Confirm that every bank account is uploaded — <em>Yes — every account is uploaded</em>
            </li>
          </ul>
        </mat-card-content>
      </mat-card>
    </section>

    <!-- ─── Context blocks (DOC-89) ──────────────────────────────────────────── -->
    <section id="context-blocks" class="sg-section" [hidden]="!shows('context-blocks')">
      <h2 class="sg-section__title">Context blocks</h2>
      <p class="sg-section__desc">
        The business's own account of itself, in named blocks, carried from the pre-application onto
        the Kickstart file. Two components, one store:
        <code>&lt;app-context-blocks-panel&gt;</code> for Ops (collapsed by default, one press
        expands; <em>Add to this</em> with the source choice; <em>Add a block</em>;
        <em>No longer needed</em> with a reason; <em>Remove block</em> on an empty block that
        mirrors nothing the business fills in — each act in its own modal, which closes only once
        the write lands; drag to the application's one order, the same for every Ops viewer; a
        neutral icon per kind of block, never a state; <em>CapX's notes</em> the one block marked,
        with a lock — visibility said only where it is the exception; under each name one line of
        facts: count, latest date, who filed) and <code>&lt;app-context-blocks-client&gt;</code> for
        the business and its agent (every shared block as <em>From you</em> / <em>From CapX</em>,
        <em>Add to this</em>; CapX's notes absent; no state word).
      </p>
      <div class="sg-callout sg-callout--warning">
        <app-icon name="info" [size]="18" />
        <span>
          Both need the context store behind them, so they are not rendered here — see the Ops
          Kickstart page and the business's submission page. Block entries are context, never
          evidence: nothing in them fills a measured field or moves a score.
        </span>
      </div>
    </section>

    <!-- ─── First look (DOC-78) ──────────────────────────────────────────────── -->
    <section id="first-look" class="sg-section" [hidden]="!shows('first-look')">
      <h2 class="sg-section__title">First look</h2>
      <p class="sg-section__desc">
        <code>&lt;app-first-look&gt;</code> — the provisional Kickstart figure on the business's and
        the agent's application page, with its basis and the amount applied for. Shown until an
        offer issues (<code>offerIssued</code>); with no figure yet it reads
        <em>Being processed — CapX will be in contact.</em>
      </p>
      <app-first-look [firstLook]="demoFirstLook" amountRequested="500000.00" />
    </section>

    <!-- ─── Tooltips ─────────────────────────────────────────────────────────── -->
    <section id="tooltips" class="sg-section" [hidden]="!shows('tooltips')">
      <h2 class="sg-section__title">Tooltips</h2>
      <div class="sg-row">
        <button mat-stroked-button matTooltip="Inverted surface, 240px max-width">Hover me</button>
        <button
          mat-icon-button
          class="sg-icon-btn"
          matTooltip="Icon buttons always need a tooltip"
          aria-label="Info"
        >
          <app-icon name="info" [size]="18" />
        </button>
      </div>
    </section>

    <!-- ─── Dividers ─────────────────────────────────────────────────────────── -->
    <section id="dividers" class="sg-section" [hidden]="!shows('dividers')">
      <h2 class="sg-section__title">Dividers</h2>
      <mat-divider />
      <div class="or-divider">
        <span class="or-divider__line"></span>
        <span class="or-divider__text">or</span>
        <span class="or-divider__line"></span>
      </div>
    </section>

    <!-- ─── Progress ─────────────────────────────────────────────────────────── -->
    <section id="progress" class="sg-section" [hidden]="!shows('progress')">
      <h2 class="sg-section__title">Progress</h2>

      <h3 class="sg-section__subtitle">Linear</h3>
      <mat-progress-bar mode="determinate" [value]="60" />
      <mat-progress-bar mode="indeterminate" />

      <h3 class="sg-section__subtitle">Circular</h3>
      <div class="sg-row">
        <mat-spinner diameter="16" />
        <mat-spinner diameter="24" />
        <mat-spinner diameter="40" />
      </div>
    </section>

    <!-- ════════════════════════════════════════════════════════════════════════
         FOUNDATIONS
         ════════════════════════════════════════════════════════════════════════ -->

    <!-- ─── Prose lists ──────────────────────────────────────────────────────── -->
    <section id="prose-lists" class="sg-section" [hidden]="!shows('prose-lists')">
      <h2 class="sg-section__title">Prose lists</h2>
      <p class="sg-section__desc">
        The global reset zeroes list padding, so a list that keeps its markers needs
        <code>.sg-list</code> (global) for the gutter. Component lists that drop markers
        (<code>&amp;__list</code>, timelines) stay flush and don't use it.
      </p>
      <ul class="sg-list">
        <li>First point, with its marker in the gutter</li>
        <li>
          Second point
          <ul>
            <li>Nested levels indent from the parent, not the card edge</li>
          </ul>
        </li>
      </ul>
    </section>

    <!-- ─── Colour ───────────────────────────────────────────────────────────── -->
    <section id="colour" class="sg-section" [hidden]="!shows('colour')">
      <h2 class="sg-section__title">Colour Palette</h2>
      <h3 class="sg-section__subtitle">Semantic tokens</h3>
      <div class="swatch-grid">
        @for (token of colorTokens; track token.name) {
          <div class="swatch">
            <div class="swatch__box" [style.background]="'var(' + token.name + ')'"></div>
            <code class="swatch__name">{{ token.name }}</code>
            <span class="swatch__label">{{ token.label }}</span>
          </div>
        }
      </div>
    </section>

    <!-- ─── Typography ───────────────────────────────────────────────────────── -->
    <section id="typography" class="sg-section" [hidden]="!shows('typography')">
      <h2 class="sg-section__title">Typography</h2>
      <div class="type-scale">
        <div class="type-sample">
          <span class="type-meta">2rem / 700 — Page H1</span>
          <p class="sg-type-h1">Page Heading</p>
        </div>
        <div class="type-sample">
          <span class="type-meta">1.5rem / 600 — Section H2</span>
          <p class="sg-type-h2">Section Heading</p>
        </div>
        <div class="type-sample">
          <span class="type-meta">1.25rem / 600 — Card H3</span>
          <p class="sg-type-h3">Card Heading</p>
        </div>
        <div class="type-sample">
          <span class="type-meta">1rem / 600 — Panel H4</span>
          <p class="sg-type-h4">Panel Heading</p>
        </div>
        <div class="type-sample">
          <span class="type-meta">0.9375rem / 400 — Body</span>
          <p class="sg-type-body">
            The quick brown fox jumps over the lazy dog. Body default copy at 15px.
          </p>
        </div>
        <div class="type-sample">
          <span class="type-meta">var(--font-size-sm) / 400 — Forms &amp; tables</span>
          <p class="sg-type-body-sm">
            The quick brown fox jumps over the lazy dog. Forms and table copy at 14px.
          </p>
        </div>
        <div class="type-sample">
          <span class="type-meta">0.8125rem / 400 — Secondary</span>
          <p class="sg-type-secondary">
            Additional context, hint text, and secondary descriptions at 13px.
          </p>
        </div>
        <div class="type-sample">
          <span class="type-meta">var(--font-size-xs) / 500 — Labels</span>
          <p class="sg-type-label">Labels &amp; Badge Text</p>
        </div>
        <div class="type-sample">
          <span class="type-meta">0.6875rem / 600 — Table headers</span>
          <p class="sg-type-table-header">Table Column Header</p>
        </div>
        <div class="type-sample">
          <span class="type-meta">var(--font-mono) — short ids, references, code</span>
          <p class="sg-type-mono">3N2Ri0U8 · DOC-89 · ZAR 454,665.60</p>
        </div>
      </div>
    </section>

    <!-- ─── Spacing ──────────────────────────────────────────────────────────── -->
    <section id="spacing" class="sg-section" [hidden]="!shows('spacing')">
      <h2 class="sg-section__title">Spacing Scale</h2>
      <p class="sg-section__desc">
        8px grid. Use <code>var(--space-*)</code> instead of literal pixel values.
      </p>
      <div class="sg-spacing-list">
        @for (token of spacingTokens; track token.name) {
          <div class="sg-spacing-row">
            <code class="sg-spacing-row__name">{{ token.name }}</code>
            <span class="sg-spacing-row__bar" [style.width]="'var(' + token.name + ')'"></span>
            <span class="sg-spacing-row__px">{{ token.px }}</span>
          </div>
        }
      </div>
    </section>

    <!-- ─── Radius ───────────────────────────────────────────────────────────── -->
    <section id="radius" class="sg-section" [hidden]="!shows('radius')">
      <h2 class="sg-section__title">Border Radius</h2>
      <p class="sg-section__desc">
        The scale is derived from <code>--radius: 0.65rem</code>. <code>--radius-pill</code> sits
        outside it — a flat <code>9999px</code> that rounds any height to a capsule. Use it for
        chips, avatars and dots; never a literal.
      </p>
      <div class="radius-row">
        @for (token of radiusTokens; track token.name) {
          <div class="radius-demo">
            <div class="radius-demo__box" [style.border-radius]="'var(' + token.name + ')'"></div>
            <code class="radius-demo__name">{{ token.label }}</code>
            <span class="radius-demo__note">{{ token.note }}</span>
          </div>
        }
      </div>
    </section>

    <!-- ─── Shadows ──────────────────────────────────────────────────────────── -->
    <section id="shadows" class="sg-section" [hidden]="!shows('shadows')">
      <h2 class="sg-section__title">Shadows / Elevation</h2>
      <div class="shadow-row">
        @for (token of shadowTokens; track token.name) {
          <div class="shadow-demo" [style.box-shadow]="'var(' + token.name + ')'">
            <code>{{ token.name }}</code>
            <span>{{ token.note }}</span>
          </div>
        }
      </div>
    </section>

    <!-- ─── Motion ───────────────────────────────────────────────────────────── -->
    <section id="motion" class="sg-section" [hidden]="!shows('motion')">
      <h2 class="sg-section__title">Motion</h2>
      <p class="sg-section__desc">
        Durations: <code>--duration-fast</code> (100ms), <code>--duration-base</code> (150ms),
        <code>--duration-slow</code> (250ms). Easing: <code>--ease-out</code> /
        <code>--ease-in</code> / <code>--ease-in-out</code>. All non-essential motion is suppressed
        under <code>prefers-reduced-motion</code> (global kill-switch in <code>styles.scss</code>).
      </p>
    </section>

    <!-- ─── Interaction states matrix ────────────────────────────────────────── -->
    <section id="states-matrix" class="sg-section" [hidden]="!shows('states-matrix')">
      <h2 class="sg-section__title">Interaction States</h2>
      <p class="sg-section__desc">
        Which states each interactive component must visually distinguish. Every focusable element
        uses a 2px <code>--focus-ring-color</code> ring at 2px offset.
      </p>
      <div class="sg-matrix-wrap">
        <table class="sg-matrix">
          <thead>
            <tr>
              <th>Component</th>
              <th>Default</th>
              <th>Hover</th>
              <th>Focus</th>
              <th>Active</th>
              <th>Disabled</th>
              <th>Loading</th>
              <th>Selected</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Primary button</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>—</td>
              <td>—</td>
            </tr>
            <tr>
              <td>Stroked button</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>—</td>
              <td>—</td>
            </tr>
            <tr>
              <td>Form field</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>—</td>
              <td>✓</td>
              <td>—</td>
              <td>—</td>
              <td>✓</td>
            </tr>
            <tr>
              <td>Table row</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>—</td>
              <td>—</td>
              <td>✓</td>
              <td>✓</td>
              <td>—</td>
            </tr>
            <tr>
              <td>Icon button</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
            </tr>
            <tr>
              <td>Clickable card</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
            </tr>
            <tr>
              <td>Filter chip</td>
              <td>✓</td>
              <td>✓</td>
              <td>✓</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
            </tr>
            <tr>
              <td>Status chip</td>
              <td>✓</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- ─── Microcopy & a11y appendix ────────────────────────────────────────── -->
    <section id="appendix" class="sg-section" [hidden]="!shows('appendix')">
      <h2 class="sg-section__title">Microcopy &amp; Accessibility</h2>
      <dl class="sg-facts sg-facts--wide">
        <div class="sg-facts__row">
          <dt>Currency</dt>
          <dd>
            <code>ZAR</code> prefix + space + thousands separator. Never "R" alone (ambiguous).
          </dd>
        </div>
        <div class="sg-facts__row">
          <dt>Dates</dt>
          <dd>
            <code>mediumDate</code> (12 Jun 2026) in lists; <code>medium</code> (with time) in
            detail/timeline. SA format DD MMM YYYY.
          </dd>
        </div>
        <div class="sg-facts__row">
          <dt>Age of an unfinished application</dt>
          <dd>
            <code>Started {{ '{{' }} created_at | ago {{ '}}' }}</code> — "Started 3 days ago",
            never a date (FLOW-394). The <code>ago</code> pipe counts viewer-local calendar days and
            lets <code>Intl.RelativeTimeFormat</code> phrase them.
          </dd>
          <dt>Null / unknown</dt>
          <dd>
            Always <code>—</code> (em dash). Never "N/A", "None", blank, or <code>null</code>.
          </dd>
        </div>
        <div class="sg-facts__row">
          <dt>Status labels</dt>
          <dd>
            Match the owning <code>*_STATUS_META</code> map verbatim — filters and chips must use
            identical text.
          </dd>
        </div>
        <div class="sg-facts__row">
          <dt>Authority labels</dt>
          <dd>
            "Analyst", "Authorised Ops", "Senior Ops", "Credit Committee", "Product Owner" — the
            rulebook's own tier names. Name the tier the rule names: an act any Authorised Ops user
            may do is not labelled "Senior Ops". Never "Manager", "Staff", "Admin".
          </dd>
        </div>
        <div class="sg-facts__row">
          <dt>Product names</dt>
          <dd>
            "Kickstart Cash" / "KS"; "Debtors Finance" / "DF". Never "loan" for DF (it's invoice
            discounting).
          </dd>
        </div>
        <div class="sg-facts__row">
          <dt>POPIA copy</dt>
          <dd>
            "Personal information" not "personal data"; "POPIA" not "GDPR". Consent names what is
            consented to — never a blanket "I agree".
          </dd>
        </div>
        <div class="sg-facts__row">
          <dt>Loading copy</dt>
          <dd>
            "Loading…" with ellipsis. Never "Please wait" or "Calculating…" for a simple fetch.
          </dd>
        </div>
        <div class="sg-facts__row">
          <dt>Icon buttons</dt>
          <dd>
            Always carry an <code>aria-label</code> + tooltip. Colour is never the only signal.
          </dd>
        </div>
      </dl>
    </section>
  </div>
</div>
